import { useEffect, useMemo } from "react";
import { FormProvider, type Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogClose, WizardDialogContent } from "@hulahoop/ui";
import { useAddInvestorSubmit } from "@/application/workflows/investors/useAddInvestorSubmit";
import { useAddInvestorWizardSteps } from "@/application/workflows/investors/useAddInvestorWizardSteps";
import { ApiClientError } from "@/application/errors/ApiClientError";
import type { Investor } from "@/domain/investor";
import { EMPTY_INVESTOR_WIZARD_FORM, isInvestorWizardStepComplete } from "@/domain/investorWizard";
import {
  createInvestorValidationMessages,
  createInvestorWizardSchema,
  INVESTOR_WIZARD_STEP_IDS,
  type InvestorWizardFormData,
  type InvestorWizardFormValues,
} from "@/domain/validation/investorSchemas";
import { useToastStore } from "@/stores/useToastStore";
import { CreateInvestorStepFields } from "../components/CreateInvestorStepFields";

export function CreateInvestorWizardModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (investor: Investor) => void;
}) {
  const { t } = useTranslation();
  const schema = useMemo(
    () => createInvestorWizardSchema(createInvestorValidationMessages(t)),
    [t],
  );
  const form = useForm<InvestorWizardFormData, unknown, InvestorWizardFormValues>({
    resolver: yupResolver(schema) as Resolver<
      InvestorWizardFormData,
      unknown,
      InvestorWizardFormValues
    >,
    mode: "onTouched",
    defaultValues: EMPTY_INVESTOR_WIZARD_FORM,
  });
  const wizard = useAddInvestorWizardSteps({ form });
  const { reset } = form;
  const { resetSteps } = wizard;
  useEffect(() => {
    if (open) {
      reset(EMPTY_INVESTOR_WIZARD_FORM);
      resetSteps();
    }
  }, [open, reset, resetSteps]);
  const showToast = useToastStore((state) => state.showToast);
  const submission = useAddInvestorSubmit({
    form,
    triggerCurrentStep: wizard.triggerCurrentStep,
    onInvestorAdded: (investor) => {
      showToast(t("investors.wizard.created", { name: investor.display_name }), "success");
      onCreated(investor);
    },
    handleClose: () => onOpenChange(false),
  });
  const { error, clearError } = submission;
  useEffect(() => {
    if (!error) return;
    showToast(
      error instanceof ApiClientError ? error.message : t("investors.errors.create_failed"),
      "error",
    );
    clearError();
  }, [error, clearError, showToast, t]);
  const steps = INVESTOR_WIZARD_STEP_IDS.map((id) => ({
    label: t(`investors.wizard.steps.${id}`),
    description: t(`investors.wizard.steps.${id}_description`),
  }));
  const last = wizard.currentStep === INVESTOR_WIZARD_STEP_IDS.length - 1;
  const values = form.watch();
  const stepComplete = isInvestorWizardStepComplete(schema, wizard.currentStep, values);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <WizardDialogContent
        title={t("investors.wizard.title")}
        description={t("investors.wizard.description")}
        steps={steps}
        value={wizard.currentStep}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button
              variant="ghost"
              disabled={wizard.currentStep === 0}
              onClick={() => wizard.goPrevious()}
            >
              {t("common.previous")}
            </Button>
            <DialogClose render={<Button variant="outline">{t("common.cancel")}</Button>} />
            {last ? (
              <Button
                disabled={submission.isSubmitting || !stepComplete}
                onClick={() => void submission.handleSubmit()}
              >
                {t("investors.wizard.create")}
              </Button>
            ) : (
              <Button disabled={!stepComplete} onClick={() => void wizard.goNext()}>
                {t("investors.wizard.continue")}
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...form}>
          <CreateInvestorStepFields step={wizard.currentStep} />
        </FormProvider>
      </WizardDialogContent>
    </Dialog>
  );
}
